import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fmtTime, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Clock, Coffee, CheckSquare, AlertCircle, FileSignature, ArrowRight, RefreshCw, CircleSlash, ShieldAlert, Receipt, Rocket, Loader2 } from "lucide-react";
import { StatusBadge } from "@/pages/employee/WeeklySummary";

const STATUS_DOT = {
  not_clocked_in: "bg-slate-300",
  working: "bg-green-500 animate-pulse",
  on_lunch: "bg-amber-500 animate-pulse",
  clocked_out: "bg-slate-500",
  submitted: "bg-sky-500",
  approved: "bg-green-600",
};
const STATUS_LABEL = {
  not_clocked_in: "Not Clocked In",
  working: "Working",
  on_lunch: "On Lunch",
  clocked_out: "Clocked Out",
  submitted: "Submitted",
  approved: "Approved",
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [missed, setMissed] = useState([]);
  const [unlocking, setUnlocking] = useState(false);
  const { user, refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();

  const isGrowth = user?.role === "super_admin" || user?.role === "growth_manager";

  const openGrowth = () => navigate("/growth");
  const unlockGrowth = async () => {
    setUnlocking(true);
    try {
      await api.post("/auth/bootstrap-super-admin");
      await refreshAuth?.();
      toast.success("Promoted to super admin — opening Growth Center.");
      navigate("/growth");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUnlocking(false);
    }
  };

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [r, m, rb] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/missed-clockouts?resolved=false").catch(() => ({ data: [] })),
        api.get("/admin/reimbursements/count-pending").catch(() => ({ data: { pending: 0 } })),
      ]);
      setData({ ...r.data, pending_reimbursements: rb.data?.pending || 0 });
      setMissed(m.data || []);
      setLastUpdated(new Date());
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Today's Activity</h1>
          <p className="text-xs text-slate-500 mt-1">
            Auto-refreshes every 30s {lastUpdated && `· last updated ${lastUpdated.toLocaleTimeString()}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isGrowth ? (
            <Button
              size="sm"
              onClick={openGrowth}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-extrabold uppercase tracking-wide"
              data-testid="open-growth-center-btn"
            >
              <Rocket className="h-4 w-4 mr-1.5" /> Growth Center
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={unlockGrowth}
              disabled={unlocking}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-extrabold uppercase tracking-wide"
              data-testid="unlock-growth-center-btn"
              title="One-time: promotes you to super admin so you can access the Growth Center"
            >
              {unlocking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />}
              Unlock Growth Center
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="refresh-dashboard-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat icon={Clock} label="Working" value={data.working} color="text-green-700" />
        <Stat icon={Coffee} label="On Lunch" value={data.on_lunch} color="text-amber-600" />
        <Stat icon={CheckSquare} label="Clocked Out" value={data.clocked_out} color="text-slate-700" />
        <Stat icon={CircleSlash} label="Not Started" value={data.not_started ?? 0} color="text-slate-500" />
        <Stat icon={AlertCircle} label="Pending Approvals" value={data.pending_approvals} color="text-red-600" actionable onClick={() => navigate("/admin/submissions?status=pending_approval")} />
        <Stat icon={ShieldAlert} label="Missed Clock-Outs" value={data.missed_clockouts ?? 0} color="text-amber-600" actionable={!!(data.missed_clockouts)} onClick={() => document.getElementById("missed-clockouts-section")?.scrollIntoView({ behavior: "smooth" })} />
        <Stat icon={FileSignature} label="Awaiting Signature" value={data.paystubs_awaiting_signature} color="text-purple-700" actionable onClick={() => navigate("/admin/paystubs")} />
        <Stat icon={Receipt} label="Pending Reimb." value={data.pending_reimbursements ?? 0} color="text-sky-700" actionable={!!(data.pending_reimbursements)} onClick={() => navigate("/admin/reimbursements")} />
      </div>

      {/* Missed Clock-Outs section */}
      {missed.length > 0 && (
        <Card id="missed-clockouts-section" className="rounded-2xl border-amber-300 bg-amber-50/30" data-testid="missed-clockouts-card">
          <CardContent className="p-0">
            <div className="px-5 py-4 border-b border-amber-200 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <h2 className="font-bold font-display text-base text-amber-900">Missed Clock-Outs ({missed.length})</h2>
                <p className="text-xs text-amber-800">Employees forgot to clock out. We auto-closed their shift at end-of-day — please verify and adjust their actual end time.</p>
              </div>
            </div>
            <div className="divide-y divide-amber-100">
              {missed.map((m) => (
                <div key={m.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`missed-row-${m.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{m.employee_name}</p>
                    <p className="text-xs text-slate-600">
                      {fmtDate(m.date)} · Clock in {fmtTime(m.clock_in)} · Auto-closed {fmtTime(m.auto_clock_out)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/admin/submissions?date=${m.date}&user=${m.user_id}`)} data-testid={`review-missed-${m.id}`}>
                      Review
                    </Button>
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700"
                      onClick={async () => {
                        try { await api.post(`/admin/missed-clockouts/${m.id}/resolve`); await load(); }
                        catch { /* silent */ }
                      }}
                      data-testid={`resolve-missed-${m.id}`}
                    >
                      Mark resolved
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Awaiting Submission - clocked out but didn't finalize */}
      {(() => {
        const awaiting = (data.today_activity || []).filter(
          (r) => r.status === "clocked_out" && !r.submission_id
        );
        if (awaiting.length === 0) return null;
        return (
          <Card className="rounded-2xl border-sky-300 bg-sky-50/40" data-testid="awaiting-submission-card">
            <CardContent className="p-0">
              <div className="px-5 py-4 border-b border-sky-200 flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-sky-700" />
                <div className="flex-1">
                  <h2 className="font-bold font-display text-base text-sky-900">Awaiting Submission ({awaiting.length})</h2>
                  <p className="text-xs text-sky-800">Clocked out but hasn't tapped "Submit Day" yet. Their hours won't appear in payroll until they sign &amp; submit.</p>
                </div>
              </div>
              <div className="divide-y divide-sky-100">
                {awaiting.map((r) => (
                  <div key={r.user_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`awaiting-row-${r.user_id}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate">{r.employee_name}</p>
                      <p className="text-xs text-slate-600">
                        Clocked in {fmtTime(r.clock_in)} · Clocked out {fmtTime(r.clock_out)}
                      </p>
                    </div>
                    <span className="text-xs text-sky-700 font-semibold shrink-0">Reminder needed</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Today's Punches - live table */}
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-bold font-display text-base">Today's Punches</h2>
              <p className="text-xs text-slate-500">Live view of every active employee's clock activity.</p>
            </div>
            <span className="text-xs text-slate-500">{data.today_activity?.length || 0} employees</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Employee</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Clock In</th>
                  <th className="text-right px-4 py-3 font-semibold">Lunch Start</th>
                  <th className="text-right px-4 py-3 font-semibold">Lunch End</th>
                  <th className="text-right px-4 py-3 font-semibold">Clock Out</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data.today_activity || []).map((row) => {
                  // Every row is clickable now — falls back to day-detail when
                  // there's no submission yet so admins can still inspect / edit.
                  const todayIso = new Date().toISOString().slice(0, 10);
                  const onRowClick = () => {
                    if (row.submission_id) navigate(`/admin/submissions/${row.submission_id}`);
                    else navigate(`/admin/submissions/day?user=${row.user_id}&date=${todayIso}`);
                  };
                  return (
                    <tr
                      key={row.user_id}
                      className="border-b border-slate-100 hover:bg-sky-50/40 cursor-pointer"
                      onClick={onRowClick}
                      data-testid={`activity-row-${row.user_id}`}
                    >
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.employee_name}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2 text-sm">
                          <span className={`h-2 w-2 rounded-full ${STATUS_DOT[row.status] || STATUS_DOT.not_clocked_in}`} />
                          {STATUS_LABEL[row.status] || row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{fmtTime(row.clock_in)}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{fmtTime(row.lunch_start)}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{fmtTime(row.lunch_end)}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">{fmtTime(row.clock_out)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs text-sky-700 font-semibold">{row.submission_id ? "Review →" : "Inspect →"}</span>
                      </td>
                    </tr>
                  );
                })}
                {(!data.today_activity || data.today_activity.length === 0) && (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No active employees.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent submissions */}
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold font-display text-base">Recent Submissions</h2>
            <button className="text-sky-700 text-sm font-semibold flex items-center gap-1" onClick={() => navigate("/admin/submissions")} data-testid="view-all-submissions">
              View all <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          {data.recent_submissions.length === 0 ? (
            <p className="py-6 text-center text-slate-500 text-sm">No recent submissions.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.recent_submissions.map((s) => (
                <button key={s.id} onClick={() => navigate(`/admin/submissions/${s.id}`)} className="w-full text-left flex items-center justify-between py-3" data-testid={`recent-${s.id}`}>
                  <div>
                    <p className="font-semibold text-slate-900">{s.employee_name}</p>
                    <p className="text-xs text-slate-500">{fmtDate(s.date)}</p>
                  </div>
                  <StatusBadge status={s.status} />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, actionable, onClick }) {
  return (
    <Card
      className={`rounded-2xl border-slate-200 ${actionable ? "cursor-pointer hover:border-sky-300 transition-colors" : ""}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <Icon className={`h-5 w-5 mb-2 ${color}`} />
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
        <p className={`text-3xl font-extrabold font-display mt-0.5 ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
